import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClipboardList, Plus, Clock, Send, CheckCircle2, XCircle, Award, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { logIA } from "@/lib/iaLogger";

const TYPE_LABELS = { devoir: "Devoir", controle: "Contrôle", examen: "Examen" };
const TYPE_COLORS = { devoir: "bg-blue-100 text-blue-800", controle: "bg-orange-100 text-orange-800", examen: "bg-red-100 text-red-800" };

export default function DevoirsPage() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openSubmit, setOpenSubmit] = useState(false);
  const [selected, setSelected] = useState(null);
  const [openSubs, setOpenSubs] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [response, setResponse] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", subject: "", class_name: "",
    type: "devoir", due_date: "", duration_minutes: 60, coefficient: 1, total_points: 20, online_response: true,
  });
  const isTeacher = user && ["teacher","admin","founder","super_admin","director"].includes(user.role);
  const isStudent = user?.role === "student";

  const load = async () => {
    try {
      const { data } = await api.get("/lms/assignments");
      setAssignments(data);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.subject || !form.class_name || !form.due_date) return toast.error("Titre, matière, classe, échéance obligatoires");
    setSaving(true);
    try {
      await api.post("/lms/assignments", { ...form, coefficient: Number(form.coefficient), total_points: Number(form.total_points), duration_minutes: Number(form.duration_minutes) });
      toast.success("Devoir créé");
      setOpenCreate(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    if (!response) return toast.error("Réponse vide");
    setSaving(true);
    try {
      const { data } = await api.post(`/lms/assignments/${selected.id}/submit`, { assignment_id: selected.id, response_text: response });
      logIA("complete_assignment", { resource_id: selected.id, resource_title: selected.title, subject: selected.subject });
      toast.success(data.is_late ? "Soumis en retard" : "Soumis avec succès !");
      setOpenSubmit(false);
      setResponse("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const viewSubmissions = async (a) => {
    setSelected(a);
    try {
      const { data } = await api.get(`/lms/assignments/${a.id}/submissions`);
      setSubmissions(data);
      setOpenSubs(true);
    } catch { toast.error("Erreur"); }
  };

  const gradeSubmission = async (subId, grade, comment) => {
    try {
      const params = new URLSearchParams({ grade: String(grade), comment: comment || "" });
      await api.post(`/lms/submissions/${subId}/grade?${params.toString()}`);
      toast.success("Note enregistrée");
      const { data } = await api.get(`/lms/assignments/${selected.id}/submissions`);
      setSubmissions(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="devoirs-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><ClipboardList className="h-3 w-3" /><span>DEVOIRS & CONTRÔLES</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Devoirs, contrôles & examens</h1>
          <p className="text-slate-500 mt-1">{assignments.length} exercice(s){isStudent && ` pour votre classe`}.</p>
        </div>
        {isTeacher && <Button onClick={() => setOpenCreate(true)} className="bg-[#166534] text-white" data-testid="btn-create-assignment"><Plus className="h-4 w-4 mr-2" />Créer un devoir</Button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {assignments.map(a => {
          const due = new Date(a.due_date);
          const past = due < new Date();
          return (
            <div key={a.id} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg" data-testid={`assign-${a.id}`}>
              <div className="flex items-start justify-between mb-3">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${TYPE_COLORS[a.type]}`}>{TYPE_LABELS[a.type]}</span>
                {isStudent && a.submitted && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700">
                    <CheckCircle2 className="h-3 w-3" />
                    {a.my_grade !== null ? `Note: ${a.my_grade}/${a.total_points}` : "Soumis"}
                  </span>
                )}
              </div>
              <div className="font-heading font-bold text-lg text-[#1E3A8A] mb-1">{a.title}</div>
              <div className="text-xs text-slate-500 mb-2"><strong>{a.subject}</strong> — {a.class_name} · Coeff {a.coefficient}</div>
              <p className="text-sm text-slate-700 mb-3 line-clamp-3">{a.description}</p>
              <div className={`text-xs flex items-center gap-1 mb-3 ${past ? "text-red-600" : "text-slate-500"}`}>
                <Clock className="h-3 w-3" />
                Échéance : {due.toLocaleString("fr-FR")}
                {a.duration_minutes && ` (${a.duration_minutes} min)`}
                {past && !a.submitted && isStudent && " — DÉLAI DÉPASSÉ"}
              </div>
              {isStudent && !a.submitted && (
                <Button size="sm" onClick={() => { setSelected(a); setOpenSubmit(true); logIA("start_assignment", { resource_id: a.id, resource_title: a.title, subject: a.subject }); }} className="bg-[#1E3A8A] text-white w-full" data-testid={`submit-${a.id}`}><Send className="h-3 w-3 mr-1" />Rendre le devoir</Button>
              )}
              {isTeacher && (
                <Button size="sm" variant="outline" onClick={() => viewSubmissions(a)} className="w-full" data-testid={`view-subs-${a.id}`}><Users className="h-3 w-3 mr-1" />Voir les copies</Button>
              )}
            </div>
          );
        })}
        {assignments.length === 0 && (
          <div className="md:col-span-2 bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500">Aucun devoir.</div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau devoir / contrôle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titre *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="dv-title" /></div>
            <div><Label>Consigne / Énoncé *</Label><Textarea rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="dv-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Matière *</Label><Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} data-testid="dv-subject" /></div>
              <div><Label>Classe *</Label><Input value={form.class_name} onChange={e => setForm({...form, class_name: e.target.value})} data-testid="dv-class" /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                  <SelectTrigger data-testid="dv-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="devoir">Devoir</SelectItem><SelectItem value="controle">Contrôle</SelectItem><SelectItem value="examen">Examen</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Échéance *</Label><Input type="datetime-local" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} data-testid="dv-duedate" /></div>
              <div><Label>Durée (min)</Label><Input type="number" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: e.target.value})} data-testid="dv-duration" /></div>
              <div><Label>Coefficient</Label><Input type="number" step="0.5" value={form.coefficient} onChange={e => setForm({...form, coefficient: e.target.value})} data-testid="dv-coef" /></div>
              <div className="col-span-2"><Label>Total points</Label><Input type="number" value={form.total_points} onChange={e => setForm({...form, total_points: e.target.value})} data-testid="dv-points" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>Annuler</Button>
            <Button onClick={create} disabled={saving} className="bg-[#166534] text-white" data-testid="dv-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit dialog */}
      <Dialog open={openSubmit} onOpenChange={setOpenSubmit}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Rendre le devoir : {selected?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {selected && (
              <div className="bg-slate-50 p-3 rounded text-sm">
                <div className="font-semibold mb-1">Consigne :</div>
                <div className="whitespace-pre-wrap">{selected.description}</div>
              </div>
            )}
            <div><Label>Votre réponse</Label><Textarea rows={10} value={response} onChange={e => setResponse(e.target.value)} placeholder="Rédigez votre réponse ici..." data-testid="sub-response" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenSubmit(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="sub-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Soumettre"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submissions view (teachers) */}
      <Dialog open={openSubs} onOpenChange={setOpenSubs}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Copies remises — {selected?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {submissions.map(s => (
              <div key={s.id} className="border border-slate-200 rounded-lg p-4" data-testid={`grade-${s.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-[#1E3A8A]">{s.student_name}</div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    {new Date(s.submitted_at).toLocaleString("fr-FR")}
                    {s.is_late && <span className="text-red-600 font-bold">(en retard)</span>}
                  </div>
                </div>
                <div className="bg-slate-50 p-3 rounded text-sm mb-3 whitespace-pre-wrap">{s.response_text}</div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <Label className="text-xs">Note /{selected?.total_points || 20}</Label>
                    <Input type="number" step="0.5" min="0" max={selected?.total_points || 20} defaultValue={s.grade || ""} id={`grade-input-${s.id}`} className="w-24" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Commentaire</Label>
                    <Input defaultValue={s.teacher_comment || ""} id={`comment-input-${s.id}`} />
                  </div>
                  <Button size="sm" className="bg-[#166534] text-white mt-4" onClick={() => {
                    const g = document.getElementById(`grade-input-${s.id}`).value;
                    const c = document.getElementById(`comment-input-${s.id}`).value;
                    if (!g) return toast.error("Note requise");
                    gradeSubmission(s.id, g, c);
                  }} data-testid={`save-grade-${s.id}`}><Award className="h-3 w-3 mr-1" />Noter</Button>
                </div>
              </div>
            ))}
            {submissions.length === 0 && <div className="text-center text-slate-400 py-6">Aucune copie remise pour l&apos;instant.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
